import { useState, useEffect } from "react";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "@/hooks/use-toast";
import logger from '@/lib/logger';

export interface WidgetLayout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardConfig {
  id: string;
  name: string;
  is_default: boolean;
  layout: WidgetLayout[];
  visible_widgets: string[];
}

const DEFAULT_LAYOUT: WidgetLayout[] = [
  { i: "active_orders", x: 0, y: 0, w: 3, h: 2 },
  { i: "in_progress_processes", x: 3, y: 0, w: 3, h: 2 },
  { i: "active_operators", x: 6, y: 0, w: 3, h: 2 },
  { i: "completion_rate", x: 9, y: 0, w: 3, h: 2 },
  { i: "alerts", x: 0, y: 2, w: 12, h: 3 },
  { i: "realtime_chart", x: 0, y: 5, w: 6, h: 4 },
  { i: "performance_chart", x: 6, y: 5, w: 6, h: 4 },
];

const DEFAULT_VISIBLE_WIDGETS = [
  "active_orders",
  "in_progress_processes",
  "active_operators",
  "completion_rate",
  "alerts",
  "realtime_chart",
  "performance_chart",
];

export const useDashboardConfig = () => {
  const [configs, setConfigs] = useState<DashboardConfig[]>([]);
  const [currentConfig, setCurrentConfig] = useState<DashboardConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      const { data, error } = await api
        .from("dashboard_configs")
        .select("*")
        .eq("user_id", user.id)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        const configs = data.map(d => ({
          ...d,
          layout: d.layout as unknown as WidgetLayout[],
          visible_widgets: d.visible_widgets as unknown as string[],
        }));
        setConfigs(configs);
        const defaultConfig = configs.find((c) => c.is_default) || configs[0];
        setCurrentConfig(defaultConfig);
      } else {
        // Create default config if none exists
        await createDefaultConfig(user.id);
      }
    } catch (error) {
      logger.error("Error fetching dashboard configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultConfig = async (userId: string) => {
    try {
      const { data, error } = await api
        .from("dashboard_configs")
        .insert({
          user_id: userId,
          name: "Vista por Defecto",
          is_default: true,
          layout: DEFAULT_LAYOUT as any,
          visible_widgets: DEFAULT_VISIBLE_WIDGETS as any,
        })
        .select()
        .single();

      if (error) throw error;

      const config = {
        ...data,
        layout: data.layout as unknown as WidgetLayout[],
        visible_widgets: data.visible_widgets as unknown as string[],
      };
      setConfigs([config]);
      setCurrentConfig(config);
    } catch (error) {
      logger.error("Error creating default config:", error);
    }
  };

  const saveConfig = async (name: string, layout: WidgetLayout[], visibleWidgets: string[], isDefault: boolean = false) => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      // If setting as default, unset other defaults
      if (isDefault) {
        await api
          .from("dashboard_configs")
          .update({ is_default: false })
          .eq("user_id", user.id);
      }

      const { data, error } = await api
        .from("dashboard_configs")
        .insert({
          user_id: user.id,
          name,
          is_default: isDefault,
          layout: layout as any,
          visible_widgets: visibleWidgets as any,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Vista guardada",
        description: `Vista "${name}" guardada exitosamente`,
      });

      await fetchConfigs();
      const config = {
        ...data,
        layout: data.layout as unknown as WidgetLayout[],
        visible_widgets: data.visible_widgets as unknown as string[],
      };
      return config;
    } catch (error: any) {
      logger.error("Error saving config:", error);
      if (error.code === "23505") {
        toast({
          title: "Error",
          description: "Ya existe una vista con ese nombre",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Error al guardar la vista",
          variant: "destructive",
        });
      }
    }
  };

  const updateConfig = async (configId: string, layout: WidgetLayout[], visibleWidgets: string[]) => {
    try {
      const { error } = await api
        .from("dashboard_configs")
        .update({
          layout: layout as any,
          visible_widgets: visibleWidgets as any,
        })
        .eq("id", configId);

      if (error) throw error;

      await fetchConfigs();
    } catch (error) {
      logger.error("Error updating config:", error);
      toast({
        title: "Error",
        description: "Error al actualizar la configuración",
        variant: "destructive",
      });
    }
  };

  const deleteConfig = async (configId: string) => {
    try {
      const { error } = await api
        .from("dashboard_configs")
        .delete()
        .eq("id", configId);

      if (error) throw error;

      toast({
        title: "Vista eliminada",
        description: "Vista eliminada exitosamente",
      });

      await fetchConfigs();
    } catch (error) {
      logger.error("Error deleting config:", error);
      toast({
        title: "Error",
        description: "Error al eliminar la vista",
        variant: "destructive",
      });
    }
  };

  const setDefaultConfig = async (configId: string) => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      // Unset all defaults
      await api
        .from("dashboard_configs")
        .update({ is_default: false })
        .eq("user_id", user.id);

      // Set new default
      const { error } = await api
        .from("dashboard_configs")
        .update({ is_default: true })
        .eq("id", configId);

      if (error) throw error;

      toast({
        title: "Vista por defecto actualizada",
        description: "Vista establecida como predeterminada",
      });

      await fetchConfigs();
    } catch (error) {
      logger.error("Error setting default config:", error);
      toast({
        title: "Error",
        description: "Error al establecer vista por defecto",
        variant: "destructive",
      });
    }
  };

  return {
    configs,
    currentConfig,
    loading,
    saveConfig,
    updateConfig,
    deleteConfig,
    setDefaultConfig,
    setCurrentConfig,
  };
};
